"use client"

import { createContext, useContext, useEffect, useState, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import i18n from "./config"

interface LanguageContextType {
  language: string
  setLanguage: (lang: string) => void
  t: (key: string, options?: any) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<string>("en")
  const { t, i18n: i18nInstance } = useTranslation()

  useEffect(() => {
    // Load language from localStorage or user preference
    const savedLanguage = localStorage.getItem("systemLanguage")
    if (savedLanguage && ["en", "si", "ta"].includes(savedLanguage)) {
      setLanguageState(savedLanguage)
      i18nInstance.changeLanguage(savedLanguage)
    } else {
      // Try to get from user profile
      fetchUserLanguage()
    }
  }, [])

  const fetchUserLanguage = async () => {
    try {
      const response = await fetch("/api/auth/me", { credentials: "include" })
      if (response.ok) {
        const user = await response.json()
        if (user.language && ["en", "si", "ta"].includes(user.language)) {
          setLanguageState(user.language)
          i18nInstance.changeLanguage(user.language)
          localStorage.setItem("systemLanguage", user.language)
        }
      }
    } catch (error) {
      console.error("Failed to fetch user language:", error)
    }
  }

  const setLanguage = (lang: string) => {
    if (["en", "si", "ta"].includes(lang)) {
      setLanguageState(lang)
      i18nInstance.changeLanguage(lang)
      localStorage.setItem("systemLanguage", lang)
    }
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}
